import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Zap, Hand, X, Printer, Loader2, ChevronDown, Sparkles, Package, Hourglass, Palette, Plus, Receipt, Trash2, Lock } from "lucide-react";

const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN")}`;
const inputCls = "bg-[#0B0F17] border-[#334155] text-slate-100 placeholder:text-slate-500";
const LABELS = { ex_showroom: "Ex-Showroom", kit: "Kit", riao_finance: "RIAO Finance", riao_cash: "RIAO Cash", seat_cover: "Seat Cover", side_bag: "Side Bag", jaal: "Jaal", helmet: "Helmet", asd_discount: "ASD Disc.", customer_discount: "Cust. Disc." };
const DEDUCT = ["asd_discount", "customer_discount"];
const PAY_FIELDS = [["received_cash", "💵 Cash"], ["received_bank", "🏦 Bank"], ["due_finance", "🏛 Due on Finance"], ["due_customer", "👤 Due on Customer"], ["extra_discount", "🏷 Discount"], ["advance", "🪙 Advance"]];
const ageCls = (d) => (d > 60 ? "border-rose-500/50 text-rose-400" : d > 30 ? "border-amber-500/50 text-amber-400" : "border-emerald-500/50 text-emerald-400");
const newDraft = () => ({ id: `d${Date.now()}${Math.floor(Math.random() * 999)}`, customer_name: "", customer_mobile: "", vehicles: [], included: {}, received_cash: "", received_bank: "", due_finance: "", due_customer: "", extra_discount: "", advance: "", remarks: "" });

const ModelFilter = ({ models, sel, setSel }) => {
  const [q, setQ] = useState("");
  const matches = models.filter((m) => !q || m.model.toLowerCase().includes(q.toLowerCase()) || (m.code || "").toLowerCase().includes(q.toLowerCase()));
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid="sell-model-filter" className={`h-9 px-3 rounded-full border text-xs flex items-center gap-1 shrink-0 ${sel.length ? "border-blue-500/60 text-blue-400 bg-blue-500/10 font-bold" : "border-[#334155] bg-[#0B0F17] text-slate-400"}`}>
          Model{sel.length ? ` (${sel.length})` : ""} <ChevronDown className="h-3 w-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-2 bg-[#111827] border-[#334155] text-slate-100" align="start">
        <Input data-testid="sell-model-search" placeholder="Search model / code…" value={q} onChange={(e) => setQ(e.target.value)} className={`${inputCls} h-8 text-xs mb-1.5`} />
        <div className="flex gap-1 mb-1.5">
          <button data-testid="sell-model-select-all" onClick={() => setSel(Array.from(new Set([...sel, ...matches.map((m) => m.model)])))} className="flex-1 text-[10px] py-1 rounded border border-blue-500/40 text-blue-400 hover:bg-blue-500/10">✓ Select all</button>
          <button data-testid="sell-model-deselect-all" onClick={() => setSel([])} className="flex-1 text-[10px] py-1 rounded border border-slate-700 text-slate-400 hover:bg-slate-800">✕ Deselect all</button>
        </div>
        <div className="max-h-56 overflow-auto">
          {matches.map((m) => (
            <label key={m.model} className="flex items-center gap-2 px-1.5 py-1.5 rounded hover:bg-slate-800 cursor-pointer text-xs">
              <Checkbox checked={sel.includes(m.model)} onCheckedChange={() => setSel(sel.includes(m.model) ? sel.filter((x) => x !== m.model) : [...sel, m.model])}
                className="border-slate-600 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600" />
              <span className="truncate flex-1">{m.model} <span className="text-slate-500 font-mono">({m.code})</span></span>
              <span className="text-[10px] font-mono text-amber-400">{m.in_stock - (m.reserved || 0)}</span>
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default function SellScreen({ isAdmin }) {
  const navigate = useNavigate();
  const [board, setBoard] = useState([]);
  const [selModels, setSelModels] = useState([]);
  const [ageFilter, setAgeFilter] = useState("all");
  const [qty, setQty] = useState({});
  const [manualOpen, setManualOpen] = useState({});
  const [stockLists, setStockLists] = useState({});
  const [drafts, setDrafts] = useState(() => {
    try { const d = JSON.parse(localStorage.getItem("sell_drafts") || "[]"); return d.length ? d : [newDraft()]; }
    catch { return [newDraft()]; }
  });
  const [active, setActive] = useState(0);
  const [history, setHistory] = useState([]);
  const [histOpen, setHistOpen] = useState(false);
  const [stale, setStale] = useState([]);
  const [delTarget, setDelTarget] = useState(null);
  const [delConfirm, setDelConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [prm, setPrm] = useState(Object.keys(LABELS));
  const [prmOpen, setPrmOpen] = useState(false);
  const boardTotal = (p) => prm.reduce((t, k) => t + (DEDUCT.includes(k) ? -Number(p?.[k] || 0) : Number(p?.[k] || 0)), 0);

  const draft = drafts[active] || drafts[0];
  useEffect(() => { localStorage.setItem("sell_drafts", JSON.stringify(drafts)); }, [drafts]);
  const loadBoard = () => api.get("/models/focus-board").then((r) => setBoard(r.data)).catch(() => {});
  const loadHistory = () => api.get("/estimates").then((r) => setHistory(r.data)).catch(() => {});
  const loadStale = () => api.get("/reservations").then((r) => setStale(r.data.filter((x) => x.stale))).catch(() => {});
  useEffect(() => { loadBoard(); loadHistory(); loadStale(); }, []);

  const patchDraft = (idx, patch) => setDrafts((ds) => ds.map((d, i) => (i === idx ? { ...d, ...patch } : d)));

  const addVehicle = useCallback((v, idx) => {
    setDrafts((ds) => ds.map((dd, i) => {
      if (i !== idx || dd.vehicles.find((x) => x.chassis_no === v.chassis_no)) return dd;
      return { ...dd, vehicles: [...dd.vehicles, v],
        included: { ...dd.included, [v.chassis_no]: Object.keys(v.pricing || {}).filter((k) => k !== "purchase_rate") } };
    }));
  }, []);

  const removeVehicle = (ch) => {
    patchDraft(active, {
      vehicles: draft.vehicles.filter((x) => x.chassis_no !== ch),
      conflicts: (draft.conflicts || []).filter((c) => c !== ch),
    });
  };

  const closeDraft = (idx) => {
    const d = drafts[idx];
    if (d.vehicles.length && !window.confirm(`Close ${d.customer_name || "draft"}? This draft will be discarded.`)) return;
    setDrafts((ds) => { const n = ds.filter((_, i) => i !== idx); return n.length ? n : [newDraft()]; });
    setActive(0);
  };

  useEffect(() => {
    const consume = async () => {
      const raw = sessionStorage.getItem("estimate_draft");
      if (!raw) return;
      sessionStorage.removeItem("estimate_draft");
      try {
        const aid = JSON.parse(raw);
        if (aid.customer_name) patchDraft(active, { customer_name: aid.customer_name });
        for (const ch of aid.chassis || []) {
          const { data } = await api.get(`/stock/search?q=${encodeURIComponent(ch)}`);
          const v = data.find((x) => x.chassis_no === ch);
          if (v && !v.reserved) addVehicle(v, active);
        }
        toast.success("AI draft loaded");
      } catch { /* ignore */ }
    };
    consume();
    window.addEventListener("prefill-estimate", consume);
    return () => window.removeEventListener("prefill-estimate", consume);
  }, [addVehicle, active]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadStock = async (model) => {
    const { data } = await api.get(`/stock/search?q=${encodeURIComponent(model)}`);
    setStockLists((s) => ({ ...s, [model]: data.filter((v) => v.model_name === model) }));
  };
  const autoPick = async (m) => {
    const n = Number(qty[m.model] || 1);
    try {
      const { data } = await api.post("/fifo/pick", { models: [m.model], count: n });
      if (!data.picked.length) { toast.error("No available (unreserved) stock"); return; }
      const { data: stock } = await api.get(`/stock/search?q=${encodeURIComponent(m.model)}`);
      for (const p of data.picked) {
        const v = stock.find((x) => x.chassis_no === p.chassis_no);
        if (v) addVehicle(v, active);
      }
      toast.success(`${data.picked.length} oldest ${m.model} added to ${draft.customer_name || "this draft"}`);
      loadBoard();
    } catch { toast.error("Auto-pick failed"); }
  };
  const toggleManual = (m) => {
    const open = !manualOpen[m.model];
    setManualOpen((s) => ({ ...s, [m.model]: open }));
    if (open) loadStock(m.model);
  };

  const toggleParam = (ch, key) => {
    const cur = draft.included[ch] || [];
    patchDraft(active, { included: { ...draft.included, [ch]: cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key] } });
  };
  const vehTotal = (v) => Object.keys(LABELS).reduce((t, k) => {
    if (!(draft.included[v.chassis_no] || []).includes(k)) return t;
    const val = Number(v.pricing?.[k] || 0);
    return t + (DEDUCT.includes(k) ? -val : val);
  }, 0);
  const grand = draft.vehicles.reduce((t, v) => t + vehTotal(v), 0);
  const paySum = PAY_FIELDS.reduce((t, [k]) => t + Number(draft[k] || 0), 0);
  const balance = grand - paySum;

  const generate = async () => {
    if (!draft.customer_name.trim()) { toast.error("Enter customer name"); return; }
    if (!draft.vehicles.length) { toast.error("Pick at least one bike"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/estimates", {
        customer_name: draft.customer_name, customer_mobile: draft.customer_mobile, draft_id: draft.id,
        chassis_list: draft.vehicles.map((v) => v.chassis_no), included: draft.included,
        received_cash: Number(draft.received_cash || 0), received_bank: Number(draft.received_bank || 0),
        due_finance: Number(draft.due_finance || 0), due_customer: Number(draft.due_customer || 0),
        extra_discount: Number(draft.extra_discount || 0), advance: Number(draft.advance || 0), remarks: draft.remarks,
      });
      toast.success(`Estimate ${data.estimate_no} created — chassis now unavailable for others`);
      setDrafts((ds) => { const n = ds.filter((_, i) => i !== active); return n.length ? n : [newDraft()]; });
      setActive(0);
      navigate(`/estimate/${data.id}`);
    } catch (e) {
      const det = e.response?.data?.detail;
      if (e.response?.status === 409 && det?.chassis) {
        patchDraft(active, { conflicts: det.chassis });
        toast.error(`${det.chassis.length} bike(s) already sold / on another estimate — highlighted in red. Remove them and pick different chassis.`);
        loadBoard();
      } else toast.error(formatApiErrorDetail(det));
    }
    finally { setBusy(false); }
  };

  const deleteEstimate = async () => {
    try {
      await api.delete(`/estimates/${delTarget.id}?confirm_no=${encodeURIComponent(delConfirm.trim())}`);
      toast.success(`${delTarget.estimate_no} deleted — chassis released back to stock`);
      setDelTarget(null); setDelConfirm("");
      loadHistory(); loadBoard(); loadStale();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const releaseStale = null; // manual release removed — delete the estimate instead
  void releaseStale;

  const shownModels = board
    .filter((m) => !selModels.length || selModels.includes(m.model))
    .filter((m) => ageFilter === "all" || (ageFilter === "old" ? m.old > 0 : m.old === 0));

  return (
    <div className="space-y-4 pb-20 lg:pb-0">
      {isAdmin && stale.length > 0 && (
        <div data-testid="stale-reservations-banner" className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-xs text-amber-300">
          ⚠️ <b>{stale.length} stale reservation(s)</b> — estimate generated 7+ days ago but sheet still shows unsold. To free a bike, delete its estimate (Recent Estimates → 🗑):
          <div className="mt-1.5 flex flex-wrap gap-2">
            {stale.map((s) => (
              <span key={s.chassis_no} className="inline-flex items-center gap-1.5 bg-slate-900/60 rounded-full px-2.5 py-1 font-mono">
                …{s.chassis_no.slice(-6)} · {s.estimate_no} · {s.age_days}d
              </span>
            ))}
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div data-testid="sell-picker" className="bg-[#1E293B] border border-[#334155] rounded-xl p-4 h-fit">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-base font-bold" style={{ fontFamily: "Outfit, sans-serif" }}>① What should I sell?</h3>
            <Button data-testid="sell-ask-ai-btn" size="sm" onClick={() => window.dispatchEvent(new CustomEvent("open-ai-chat"))}
              className="bg-indigo-600 hover:bg-indigo-500 text-white h-8"><Sparkles className="h-3.5 w-3.5" /><span className="ml-1 text-xs">Ask AI</span></Button>
          </div>
          <div className="flex gap-2 mb-3 overflow-x-auto no-scrollbar">
            <ModelFilter models={board} sel={selModels} setSel={setSelModels} />
            {[["all", "All ages"], ["old", "⏳ 60+d only"], ["fresh", "🟢 Fresh"]].map(([v, l]) => (
              <button key={v} data-testid={`sell-age-${v}`} onClick={() => setAgeFilter(v)}
                className={`h-9 px-3 rounded-full border text-xs whitespace-nowrap ${ageFilter === v ? "border-blue-500/60 text-blue-400 bg-blue-500/10 font-bold" : "border-[#334155] bg-[#0B0F17] text-slate-400"}`}>{l}</button>
            ))}
            <button data-testid="sell-param-toggle" onClick={() => setPrmOpen(!prmOpen)}
              className="sm:hidden h-9 px-3 rounded-full border border-cyan-500/50 text-cyan-400 bg-cyan-500/10 text-xs font-bold whitespace-nowrap">
              ⚙ Price ({prm.length}/{Object.keys(LABELS).length}) {prmOpen ? "▴" : "▾"}
            </button>
          </div>
          <div data-testid="price-includes-bar" className={`${prmOpen ? "block" : "hidden"} sm:block rounded-xl border border-cyan-500/30 bg-cyan-500/5 p-2.5 mb-3`}>
            <p className="text-[9px] font-extrabold uppercase tracking-widest text-cyan-400 mb-1.5">Price includes — tick/untick, totals update live</p>
            <div className="flex flex-wrap items-center gap-1.5">
              {Object.entries(LABELS).map(([k, l]) => (
                <button key={k} data-testid={`price-param-${k}`} onClick={() => setPrm(prm.includes(k) ? prm.filter((x) => x !== k) : [...prm, k])}
                  className={`text-[10px] px-2.5 py-1 rounded-full border transition-colors ${prm.includes(k)
                    ? "border-cyan-500/50 text-cyan-400 bg-cyan-500/10" : "border-slate-700 text-slate-500 line-through"}`}>
                  {prm.includes(k) ? "✓ " : ""}{l}
                </button>
              ))}
              <button data-testid="price-param-all" onClick={() => setPrm(Object.keys(LABELS))} className="text-[10px] font-bold text-cyan-400 ml-1 hover:underline">Select all</button>
              <span className="text-slate-600 text-[10px]">·</span>
              <button data-testid="price-param-none" onClick={() => setPrm([])} className="text-[10px] font-bold text-slate-400 hover:underline">Deselect all</button>
            </div>
          </div>
          <div className="space-y-2.5 max-h-[560px] overflow-y-auto pr-1">
            {shownModels.map((m, i) => {
              const avail = m.in_stock - (m.reserved || 0);
              return (
                <div key={m.model} data-testid={`sell-model-${i}`}
                  className={`rounded-xl border p-3 ${m.focus === "sell_first" ? "border-rose-500/40 bg-rose-500/5" : m.focus === "ageing" ? "border-amber-500/30 bg-amber-500/5" : "border-[#334155] bg-[#111827]"}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-bold text-sm text-slate-100">
                      {m.focus === "sell_first" ? "🔴 " : m.focus === "ageing" ? "🟠 " : "🟢 "}{m.model} <span className="text-slate-500 text-xs font-mono">({m.code})</span>
                      {m.focus === "sell_first" && <Badge className="ml-1.5 bg-rose-500/15 text-rose-400 border border-rose-500/40 text-[8px] font-extrabold align-middle">SELL FIRST</Badge>}
                    </div>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button data-testid={`sell-price-${i}`} className="font-mono font-extrabold text-emerald-400 text-base underline decoration-dotted underline-offset-4 shrink-0">
                          {Object.keys(m.pricing || {}).length ? inr(boardTotal(m.pricing)) : "—"}
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-60 p-3 bg-[#0B0F17] border-[#334155] text-slate-100 text-xs" align="end">
                        <p className="font-bold mb-2">{m.model} ({m.code}) — ticked only</p>
                        {Object.entries(LABELS).filter(([k]) => prm.includes(k) && m.pricing?.[k]).map(([k, l]) => (
                          <div key={k} className="flex justify-between py-1 border-b border-slate-800/60">
                            <span className="text-slate-400">{l}</span>
                            <span className={`font-mono ${DEDUCT.includes(k) ? "text-rose-400" : "text-slate-200"}`}>{DEDUCT.includes(k) ? "−" : ""}{inr(m.pricing[k])}</span>
                          </div>
                        ))}
                        <div className="flex justify-between pt-2 font-bold text-emerald-400"><span>On-road Total</span><span className="font-mono">{inr(boardTotal(m.pricing))}</span></div>
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="flex gap-1.5 mt-1.5 flex-wrap items-center">
                    <Popover>
                      <PopoverTrigger asChild>
                        <button data-testid={`sell-stock-${i}`}>
                          <Badge variant="outline" className="border-blue-500/40 text-blue-400 text-[10px] gap-1 underline decoration-dotted underline-offset-2 cursor-pointer"><Package className="h-3 w-3" />{avail} avail{m.reserved ? ` · ${m.reserved} res.` : ""}</Badge>
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-60 p-3 bg-[#0B0F17] border-[#334155] text-slate-100 text-xs" align="start">
                        <p className="font-bold mb-2">{m.model} — colours in stock</p>
                        {m.colours.map((c) => (
                          <div key={c.colour} className="flex justify-between py-1 border-b border-slate-800/60">
                            <span className="text-slate-300">{c.colour} × {c.count}</span>
                            <span className={`font-mono ${c.oldest > 60 ? "text-rose-400" : c.oldest > 30 ? "text-amber-400" : "text-emerald-400"}`}>{c.oldest}d</span>
                          </div>
                        ))}
                      </PopoverContent>
                    </Popover>
                    <Badge variant="outline" className={`text-[10px] gap-1 ${ageCls(m.oldest_age)}`}><Hourglass className="h-3 w-3" />oldest {m.oldest_age}d</Badge>
                    <Badge variant="outline" className="border-slate-600 text-slate-400 text-[10px] gap-1"><Palette className="h-3 w-3" />{m.colours.length}</Badge>
                    <span className="inline-flex h-2 w-20 rounded-full overflow-hidden bg-slate-800 ml-1">
                      <span className="bg-emerald-500" style={{ width: `${(m.fresh / (m.in_stock || 1)) * 100}%` }} />
                      <span className="bg-amber-500" style={{ width: `${(m.watch / (m.in_stock || 1)) * 100}%` }} />
                      <span className="bg-rose-500" style={{ width: `${(m.old / (m.in_stock || 1)) * 100}%` }} />
                    </span>
                  </div>
                  <div className="flex gap-2 mt-2.5">
                    <Input data-testid={`sell-qty-${i}`} type="number" min="1" value={qty[m.model] ?? 1}
                      onChange={(e) => setQty((s) => ({ ...s, [m.model]: e.target.value }))} className={`${inputCls} w-16 h-9 text-center font-bold`} />
                    <Button data-testid={`sell-autopick-${i}`} size="sm" onClick={() => autoPick(m)} disabled={avail < 1}
                      className="flex-1 bg-orange-600 hover:bg-orange-500 text-white h-9 font-bold text-xs disabled:opacity-40">
                      <Zap className="h-3.5 w-3.5 mr-1" /> Auto-pick oldest
                    </Button>
                    <Button data-testid={`sell-manual-${i}`} size="sm" variant="outline" onClick={() => toggleManual(m)}
                      className="h-9 border-orange-500/50 text-orange-400 hover:bg-orange-500/10 text-xs font-bold">
                      <Hand className="h-3.5 w-3.5" /><ChevronDown className={`h-3 w-3 ml-0.5 transition-transform ${manualOpen[m.model] ? "rotate-180" : ""}`} />
                    </Button>
                  </div>
                  {manualOpen[m.model] && (
                    <div className="mt-2 pt-2 border-t border-dashed border-slate-700 max-h-44 overflow-y-auto">
                      {(stockLists[m.model] || []).map((v) => {
                        const inDraft = !!draft.vehicles.find((x) => x.chassis_no === v.chassis_no);
                        const lockedByOther = v.reserved && v.reserved_draft_id !== draft.id;
                        return (
                          <label key={v.chassis_no} data-testid={`sell-chassis-${v.chassis_no}`}
                            className={`flex items-center gap-2 py-1.5 text-xs rounded px-1 ${lockedByOther ? "opacity-50" : "cursor-pointer hover:bg-slate-800/50"}`}>
                            <Checkbox checked={inDraft} disabled={lockedByOther}
                              onCheckedChange={() => (inDraft ? removeVehicle(v.chassis_no) : addVehicle(v, active))}
                              className="border-slate-600 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600" />
                            <span className="font-mono text-orange-400">…{v.chassis_no.slice(-6)}</span>
                            <span className="text-slate-400 truncate flex-1">{v.colour || "—"}</span>
                            {lockedByOther && <span className="flex items-center gap-1 text-[9px] text-amber-400"><Lock className="h-2.5 w-2.5" />{v.reserved_for || v.reserved_status}</span>}
                            <Badge variant="outline" className={`text-[9px] font-mono ${ageCls(v.age_days)}`}>{v.age_days}d</Badge>
                          </label>
                        );
                      })}
                      {!(stockLists[m.model] || []).length && <p className="text-[11px] text-slate-500 py-2 text-center">Loading…</p>}
                    </div>
                  )}
                </div>
              );
            })}
            {!shownModels.length && <p className="text-xs text-slate-500 text-center py-6">No models match the filter.</p>}
          </div>
        </div>

        <div className={sheetOpen
          ? "fixed inset-x-0 bottom-0 z-50 max-h-[85vh] overflow-y-auto rounded-t-2xl bg-[#0B0F17] border-t-2 border-[#334155] p-3 space-y-4 lg:static lg:z-auto lg:max-h-none lg:overflow-visible lg:rounded-none lg:bg-transparent lg:border-0 lg:p-0"
          : "hidden lg:block space-y-4"}>
          {sheetOpen && (
            <button data-testid="sheet-grab" onClick={() => setSheetOpen(false)} className="lg:hidden w-full flex justify-center pt-1 pb-2">
              <span className="w-12 h-1.5 rounded-full bg-slate-600" />
            </button>
          )}
          <div data-testid="sell-estimate" className="bg-[#1E293B] border border-[#334155] rounded-xl p-4">
            <div data-testid="draft-tabs" className="flex gap-1.5 mb-3 overflow-x-auto no-scrollbar items-center">
              {drafts.map((d, i) => (
                <button key={d.id} data-testid={`draft-tab-${i}`} onClick={() => setActive(i)}
                  className={`flex items-center gap-1.5 shrink-0 rounded-lg px-3 py-2 text-xs font-bold transition-colors ${i === active ? "bg-emerald-600 text-white" : "bg-[#111827] border border-[#334155] text-slate-400 hover:text-white"}`}>
                  👤 {d.customer_name || `Draft ${i + 1}`}
                  <span className={`rounded-full px-1.5 text-[9px] ${i === active ? "bg-white/20" : "bg-slate-800"}`}>{d.vehicles.length}</span>
                  <X data-testid={`draft-close-${i}`} className="h-3 w-3 opacity-70 hover:opacity-100" onClick={(e) => { e.stopPropagation(); closeDraft(i); }} />
                </button>
              ))}
              {drafts.length < 5 && (
                <button data-testid="draft-new-btn" onClick={() => { setDrafts((ds) => [...ds, newDraft()]); setActive(drafts.length); }}
                  className="shrink-0 flex items-center gap-1 rounded-lg border border-dashed border-slate-600 px-3 py-2 text-xs font-bold text-slate-400 hover:text-white hover:border-slate-400">
                  <Plus className="h-3 w-3" /> New customer
                </button>
              )}
            </div>
            <div className="flex gap-2 mb-3">
              <Input data-testid="sell-customer-input" placeholder="👤 Customer name" value={draft.customer_name}
                onChange={(e) => patchDraft(active, { customer_name: e.target.value })} className={`${inputCls} h-10`} />
              <Input data-testid="sell-mobile-input" placeholder="📱 Mobile (WhatsApp)" value={draft.customer_mobile}
                onChange={(e) => patchDraft(active, { customer_mobile: e.target.value })} className={`${inputCls} h-10 w-44`} />
            </div>
            {draft.vehicles.map((v) => {
              const isConflict = (draft.conflicts || []).includes(v.chassis_no);
              return (
              <div key={v.chassis_no} data-testid={`estimate-vehicle-${v.chassis_no}`} className={`rounded-xl border p-3 mb-2.5 ${isConflict ? "border-rose-500 bg-rose-500/10" : "border-[#334155] bg-[#111827]"}`}>
                {isConflict && (
                  <div data-testid={`conflict-badge-${v.chassis_no}`} className="mb-2 rounded-lg bg-rose-500/20 border border-rose-500/50 px-2.5 py-1.5 text-[10px] font-bold text-rose-300">
                    🚫 SOLD / ON ANOTHER ESTIMATE — remove this bike and select a different chassis
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <p className="font-bold text-xs text-slate-100">{v.model_name} <span className="font-mono text-orange-400">…{v.chassis_no.slice(-6)}</span> <span className="text-slate-500">{v.colour}</span></p>
                  <span className="flex items-center gap-2">
                    <button data-testid={`estimate-params-all-${v.chassis_no}`} onClick={() => patchDraft(active, { included: { ...draft.included, [v.chassis_no]: Object.keys(LABELS) } })} className="text-[10px] text-emerald-400 hover:underline">✓All</button>
                    <button data-testid={`estimate-params-none-${v.chassis_no}`} onClick={() => patchDraft(active, { included: { ...draft.included, [v.chassis_no]: [] } })} className="text-[10px] text-slate-400 hover:underline">✕None</button>
                    <button onClick={() => removeVehicle(v.chassis_no)} className="text-slate-500 hover:text-rose-400"><X className="h-4 w-4" /></button>
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 mt-2">
                  {Object.entries(LABELS).map(([k, label]) => {
                    const val = Number(v.pricing?.[k] || 0);
                    if (!val) return null;
                    const inc = (draft.included[v.chassis_no] || []).includes(k);
                    return (
                      <label key={k} className="flex items-center justify-between gap-1 text-[11px] cursor-pointer">
                        <span className="flex items-center gap-1.5">
                          <Checkbox checked={inc} onCheckedChange={() => toggleParam(v.chassis_no, k)}
                            className="border-slate-600 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600 h-3.5 w-3.5" />
                          <span className={inc ? "text-slate-300" : "text-slate-600 line-through"}>{label}</span>
                        </span>
                        <span className={`font-mono ${DEDUCT.includes(k) ? "text-rose-400" : "text-slate-300"} ${!inc && "opacity-40"}`}>{DEDUCT.includes(k) ? "−" : ""}{inr(val)}</span>
                      </label>
                    );
                  })}
                </div>
                <p className="text-right text-sm font-mono font-bold text-emerald-400 mt-1.5 pt-1.5 border-t border-slate-800">{inr(vehTotal(v))}</p>
              </div>
            );})}
            {!draft.vehicles.length && <p className="text-xs text-slate-500 text-center py-5">⚡ Auto-pick or 👆 choose bikes on the left to add them for <b>{draft.customer_name || "this customer"}</b>.</p>}
            {draft.vehicles.length > 0 && (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-2">
                  {PAY_FIELDS.map(([k, l]) => (
                    <div key={k}>
                      <label className="text-[9px] uppercase tracking-widest text-slate-400">{l}</label>
                      <Input data-testid={`pay-${k}`} type="number" min="0" value={draft[k]} onChange={(e) => patchDraft(active, { [k]: e.target.value })} className={`${inputCls} h-9 mt-0.5`} placeholder="0" />
                    </div>
                  ))}
                </div>
                <div className="mb-2">
                  <label className="text-[9px] uppercase tracking-widest text-slate-400">📝 Remarks / Notes</label>
                  <Input data-testid="pay-remarks" value={draft.remarks || ""} onChange={(e) => patchDraft(active, { remarks: e.target.value })}
                    className={`${inputCls} h-9 mt-0.5`} placeholder="e.g. RC pending, accessories promised, delivery date…" />
                </div>
                <div data-testid="balance-calc" className="rounded-xl border border-[#334155] bg-[#111827] px-3 py-2 text-[11px] mb-2">
                  <div className="flex justify-between font-bold text-slate-200 border-b border-slate-800 pb-1 mb-1">
                    <span>Total ({draft.vehicles.length} bike{draft.vehicles.length > 1 ? "s" : ""})</span><span className="font-mono">{inr(grand)}</span>
                  </div>
                  {PAY_FIELDS.filter(([k]) => Number(draft[k] || 0) > 0).map(([k, l]) => (
                    <div key={k} className="flex justify-between text-slate-400 py-0.5">
                      <span>− {l.replace(/^\S+\s/, "")}</span><span className="font-mono text-rose-400">{inr(draft[k])}</span>
                    </div>
                  ))}
                </div>
                <div data-testid="sell-total-bar" className="flex items-center justify-between rounded-xl bg-slate-950 border border-slate-700 px-4 py-3">
                  <div>
                    <p className="text-[9px] uppercase tracking-widest text-slate-400 font-bold">Balance Left</p>
                    <p data-testid="balance-left" className={`font-mono font-extrabold text-2xl ${balance > 0 ? "text-emerald-400" : balance < 0 ? "text-rose-400" : "text-slate-300"}`}>{inr(balance)}</p>
                  </div>
                  <Button data-testid="estimate-generate-btn" onClick={generate} disabled={busy}
                    className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold h-11 px-5 active:scale-95">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}<span className="ml-1.5">Generate</span>
                  </Button>
                </div>
              </>
            )}
          </div>
          <div data-testid="estimate-history" className="bg-[#1E293B] border border-[#334155] rounded-xl">
            <button data-testid="history-toggle" onClick={() => setHistOpen(!histOpen)} className="w-full flex items-center gap-3 px-4 py-3">
              <span className="h-8 w-8 rounded-lg bg-emerald-500/15 text-emerald-400 flex items-center justify-center"><Receipt className="h-4 w-4" /></span>
              <span className="text-sm font-bold" style={{ fontFamily: "Outfit, sans-serif" }}>Recent Estimates</span>
              <Badge className="bg-emerald-600 text-white text-[10px]">{history.length}</Badge>
              <ChevronDown className={`h-4 w-4 text-slate-500 ml-auto transition-transform ${histOpen ? "rotate-180" : ""}`} />
            </button>
            {histOpen && (
              <div className="px-4 pb-3 space-y-1.5 max-h-72 overflow-y-auto">
                {history.map((h, i) => (
                  <div key={h.id} data-testid={`estimate-history-${i}`} className="flex items-center gap-2 rounded-lg border border-[#334155] bg-[#111827] px-3 py-2 text-xs">
                    <button onClick={() => navigate(`/estimate/${h.id}`)} className="flex-1 flex justify-between items-center gap-2 text-left hover:text-white">
                      <span className="font-mono text-emerald-400">{h.estimate_no}</span>
                      {h.sold_outside?.length > 0 && <Badge variant="outline" className="border-rose-500/50 text-rose-400 text-[8px] shrink-0" title={h.sold_outside.map((s) => `${s.chassis_no} sold to ${s.sold_to}`).join("; ")}>⚠ sold outside</Badge>}
                      <span className="text-slate-300 truncate flex-1">{h.customer_name}</span>
                      <span className="font-mono text-slate-300">{inr(h.net_total)}</span>
                    </button>
                    {isAdmin && (
                      <button data-testid={`estimate-delete-${i}`} onClick={() => { setDelTarget(h); setDelConfirm(""); }} className="text-slate-600 hover:text-rose-400" title="Delete estimate">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                ))}
                {!history.length && <p className="text-xs text-slate-500 pb-2">No estimates yet.</p>}
              </div>
            )}
          </div>
        </div>
      </div>

      {sheetOpen && <div data-testid="sheet-backdrop" className="lg:hidden fixed inset-0 bg-black/60 z-40" onClick={() => setSheetOpen(false)} />}
      {!sheetOpen && (
        <div data-testid="sell-mobile-bar" className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-slate-900 border-t border-slate-700 px-4 py-2.5 flex items-center gap-3">
          <div className="flex-1">
            <p className="text-[9px] text-slate-400">👤 {draft.customer_name || "Draft"} · {draft.vehicles.length} bike(s)</p>
            <p className="font-mono font-extrabold text-emerald-400 text-lg leading-none">{inr(balance)} <span className="text-[9px] text-slate-500 font-sans">balance</span></p>
          </div>
          <Button data-testid="sell-mobile-open-sheet" onClick={() => setSheetOpen(true)} className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold h-10 px-5">
            Estimate ▴
          </Button>
        </div>
      )}

      <Dialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <DialogContent data-testid="delete-estimate-dialog" className="bg-[#111827] border-[#334155] text-slate-100 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-rose-400 flex items-center gap-2"><Trash2 className="h-4 w-4" /> Delete {delTarget?.estimate_no}?</DialogTitle>
            <DialogDescription className="text-slate-400 text-xs">
              {delTarget?.customer_name} · {inr(delTarget?.net_total)} · {delTarget?.vehicle_count} vehicle(s).<br />
              This releases the reserved chassis back to available stock. <b>This cannot be undone.</b>
            </DialogDescription>
          </DialogHeader>
          <p className="text-xs text-slate-300">Type <b className="font-mono text-rose-400">{delTarget?.estimate_no}</b> to confirm:</p>
          <Input data-testid="delete-confirm-input" value={delConfirm} onChange={(e) => setDelConfirm(e.target.value)}
            placeholder={delTarget?.estimate_no} className={`${inputCls} font-mono`} />
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="ghost" onClick={() => setDelTarget(null)} className="text-slate-400 hover:text-white hover:bg-slate-800">Cancel</Button>
            <Button data-testid="delete-confirm-btn" size="sm" onClick={deleteEstimate} disabled={delConfirm.trim().toUpperCase() !== delTarget?.estimate_no}
              className="bg-rose-600 hover:bg-rose-500 text-white disabled:opacity-40">Yes, delete permanently</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
